'use client';

import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useUser } from 'app/_providers/User';
import useFetcher from 'app/_utils/data/useFetcher';

import React from 'react';
import { AccountState, Organization } from 'shared/src/generated/openapi/v2/types';
import useSWR, { KeyedMutator } from 'swr';

export type IOrganizationContext = Organization;

export type OrganizationContextValue = IOrganizationContext & {
  mutate: KeyedMutator<Organization>;
  isLoading: boolean;
};

export type UseOrganizationReturn = OrganizationContextValue;

const OrganizationContext = React.createContext<OrganizationContextValue>({} as OrganizationContextValue);

const defaultContext: Organization = {
  accountId: '',
  created: '',
  updated: '',
  state: undefined as unknown as AccountState,
  organizationId: '',
  organizationAlias: '',
  license: undefined,
  region: '',
  maxWorkspaces: 0,
  capabilities: {
    'cloud.iam.teams': { enabled: false },
    'cloud.iam.sso': { enabled: false },
    'cloud.iam.eventlogs': { enabled: false },
    'cloud.iam.fineGrainedRoles': { enabled: false },
    'cloud.workspace.provisioning': { enabled: false },
    'cloud.workspace.multiRegion': { enabled: false },
  },
};

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { authorizedOrganizationId } = useAuthProvider();
  const { memberships } = useUser();
  const [organization, setOrganization] = React.useState<IOrganizationContext | undefined>(undefined);
  const [resolvedAuthId, setResolvedAuthId] = React.useState<string | null | undefined>(undefined);
  const { user, isLoading: isAuthLoading } = useCommonAuthProvider();
  const { fetcher } = useFetcher();
  const { data, mutate, isLoading: isSwrLoading } = useSWR<Organization>(
    authorizedOrganizationId ? `${process.env.ZEUS_API_URL}/v1/tenants/${authorizedOrganizationId}/organization` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const isOrganizationLoading =
    Boolean(authorizedOrganizationId) &&
    (isAuthLoading || isSwrLoading || resolvedAuthId !== authorizedOrganizationId);

  React.useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const currentAuthorizedMembership = memberships?.data?.find(
      (membership) => membership.organizationId === authorizedOrganizationId)
      ;

    if (currentAuthorizedMembership?.organization || data) {
      setOrganization({
        ...data,
        organizationId: currentAuthorizedMembership?.organizationId || data?.organizationId || '',
        organizationAlias: currentAuthorizedMembership?.organization.organizationAlias || data?.organizationAlias,
      } as IOrganizationContext);
    } else {
      // user is not logged into an organization
      setOrganization(undefined);
    }

    setResolvedAuthId(authorizedOrganizationId);
  }, [user, isAuthLoading, data, authorizedOrganizationId, memberships?.data]);

  return (
    <OrganizationContext.Provider
      value={{
        ...defaultContext,
        ...(organization || {}),
        mutate,
        isLoading: isOrganizationLoading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export function useOrganization(): UseOrganizationReturn {
  const context = React.useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error('useOrganization must be used within a OrganizationProvider');
  }

  return context;
}
